import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Trash2, FolderOpen } from 'lucide-react';
import { toast } from 'sonner';
import { getEntry, updateEntry, deleteEntry } from '../lib/entries';
import { getProject } from '../../projects/lib/projects';
import { NotebookEditor } from '../components/NotebookEditor';
import type { Entry, Project, TextContent } from '../../../shared/lib/supabase';
import { debounce } from 'lodash';

interface EntryDetailPageProps {
  entryId?: string;
}

export function EntryDetailPage({ entryId: propEntryId }: EntryDetailPageProps = {}) {
  const { entryId: paramEntryId } = useParams<{ entryId: string }>();
  const entryId = propEntryId || paramEntryId;
  const navigate = useNavigate();
  const [entry, setEntry] = useState<Entry | null>(null);
  const [project, setProject] = useState<Project | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [content, setContent] = useState<any>(null);
  const [entryName, setEntryName] = useState('');
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [lastSaved, setLastSaved] = useState<Date | null>(null);

  useEffect(() => {
    if (entryId) {
      loadEntry();
    }
  }, [entryId]);

  const loadEntry = async () => {
    try {
      setLoading(true);
      const entryData = await getEntry(entryId!);
      
      if (!entryData) {
        toast.error('Entry not found');
        navigate('/entries');
        return;
      }

      setEntry(entryData);
      setEntryName(entryData.name);
      setContent(entryData.content || null);

      // Load project
      const projectData = await getProject(entryData.project_id);
      if (projectData) {
        setProject(projectData);
      }
    } catch (error) {
      console.error('Error loading entry:', error);
      toast.error('Failed to load entry');
      navigate('/entries');
    } finally {
      setLoading(false);
    }
  };

  // Auto-save function with debouncing
  const autoSave = useCallback(
    debounce(async (contentToSave: any, nameToSave: string) => {
      if (!entry) return;

      try {
        setSaving(true);
        console.log('🔄 Auto-saving entry...');

        await updateEntry(entry.id, {
          name: nameToSave,
          content: contentToSave
        });

        setLastSaved(new Date());
        console.log('✅ Entry auto-saved successfully');
      } catch (error) {
        console.error('❌ Auto-save failed:', error);
        // Don't show toast for auto-save failures to avoid spam
        console.warn('Auto-save failed, will retry on next change');
      } finally {
        setSaving(false);
      }
    }, 1000), // 1 second debounce
    [entry]
  );

  // Handle content changes from the NotebookEditor
  const handleContentChange = useCallback((newContent: any) => {
    setContent(newContent);
    
    // Trigger auto-save with current content and name
    if (entryName) {
      autoSave(newContent, entryName);
    }
  }, [autoSave, entryName]);

  // Handle name changes
  const handleNameChange = (newName: string) => {
    setEntryName(newName);
    
    // Trigger auto-save with current name and content
    if (content) {
      autoSave(content, newName);
    }
  };

  const handleDelete = async () => {
    if (!entry) return;

    try {
      await deleteEntry(entry.id);
      toast.success('Entry deleted successfully');
      navigate('/entries');
    } catch (error) {
      console.error('Error deleting entry:', error);
      toast.error('Failed to delete entry');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center bg-white w-full h-full min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-400"></div>
      </div>
    );
  }

  if (!entry) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-600">Entry not found</p>
        <Link to="/entries" className="text-blue-600 hover:text-blue-700 mt-4 inline-block">
          Back to Entries
        </Link>
      </div>
    );
  }

  return (
    <div className="w-full p-6 bg-white">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex-1 mr-6">
            <input
              type="text"
              value={entryName}
              onChange={(e) => handleNameChange(e.target.value)}
              className="text-3xl font-bold text-gray-900 bg-transparent border-none focus:outline-none focus:ring-2 focus:ring-blue-500 rounded px-2 -ml-2 w-full"
              placeholder="Entry name..."
            />
            {project && (
              <div className="flex items-center gap-2 mt-2">
                <FolderOpen className="h-4 w-4 text-gray-400" />
                <Link 
                  to={`/projects/${project.id}`}
                  className="text-sm text-gray-500 hover:text-gray-700"
                >
                  {project.name}
                </Link>
              </div>
            )}
          </div>
          
          <div className="flex items-center gap-3">
            {/* Delete Button */}
            <button
              onClick={() => setShowDeleteDialog(true)}
              className="p-2 text-red-600 hover:bg-red-50 rounded-sm transition-colors"
              title="Delete entry"
            >
              <Trash2 className="h-5 w-5" />
            </button>
          </div>
        </div>
      </div>

      {/* Enhanced Editor with Real-time Collaboration and Auto-save */}
      <div className="bg-white rounded-sm border border-gray-200" style={{ height: 'calc(100vh - 200px)' }}>
        <NotebookEditor 
          initialContent={content} 
          onChange={handleContentChange}
          entryId={entryId}
          projectId={entry?.project_id}
          singlePageMode={true}
        />
      </div>

      {/* Delete Confirmation Dialog */}
      {showDeleteDialog && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-sm p-6 w-full max-w-md">
            <h2 className="text-xl font-semibold mb-4 text-red-600">Delete Entry</h2>
            
            <p className="text-gray-700 mb-6">
              Are you sure you want to delete "<strong>{entry.name}</strong>"? 
              This action cannot be undone.
            </p>

            <div className="flex gap-3">
              <button
                onClick={() => setShowDeleteDialog(false)}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleDelete}
                className="flex-1 px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors"
              >
                Delete Entry
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}